import {
	ChevronDownIcon,
	ChevronLeftIcon,
	Chip,
	cn,
	IconButton,
} from "@au-schein-de/au-ui-library";
import {
	type KeyboardEvent,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { PHARMACY_PICKER_RADIUS_PANEL_TEST_ID } from "~/features/products/pharmacy-picker/pharmacy-picker-radius-panel.constants";
import { usePanelOutsideClose } from "~/hooks/use-panel-outside-close.hook";
import {
	ANY_RADIUS,
	buildRadiusOptions,
	type PharmacyWithDistance,
	type RadiusOptionValue,
} from "./radius-options";
import { RadiusSelectSheet } from "./radius-select-sheet.component";

/** Figma 547 "Prominent" drop shadow, as used by the pharmacy select variant. */
const PROMINENT_SHADOW = "shadow-[0px_10px_20px_0px_rgba(33,42,54,0.16)]";

const RADIUS_PANEL_WIDTH_PX = 228;
const RADIUS_PANEL_GAP_PX = 8;

function panelStyleForAnchor(anchor: DOMRect): { top: number; left: number } {
	const fitsRight =
		anchor.right + RADIUS_PANEL_GAP_PX + RADIUS_PANEL_WIDTH_PX <=
		window.innerWidth;
	return {
		top: anchor.top,
		left: fitsRight
			? anchor.right + RADIUS_PANEL_GAP_PX
			: Math.max(
					RADIUS_PANEL_GAP_PX,
					anchor.left - RADIUS_PANEL_GAP_PX - RADIUS_PANEL_WIDTH_PX,
				),
	};
}

function pickerContentForAnchor(anchor: HTMLDivElement | null): Element | null {
	return (
		anchor?.closest("[data-testid='pharmacy-picker-panel-slot']") ??
		anchor?.closest("[data-testid='pharmacy-picker-content']") ??
		null
	);
}

function activateOnEnterOrSpace(
	event: KeyboardEvent<HTMLElement>,
	action: () => void,
): void {
	if (event.key === "Enter" || event.key === " ") {
		event.preventDefault();
		action();
	}
}

interface RadiusSelectProps {
	value: RadiusOptionValue;
	/**
	 * Pharmacies the counts are derived from. Pass the set already narrowed to
	 * the active pharmacy-type selection (Top / non-Top): per Figma, a radius
	 * row is disabled when that selection has no pharmacy inside it.
	 */
	pharmacyMarkers: readonly PharmacyWithDistance[];
	onChange: (value: RadiusOptionValue) => void;
	/**
	 * Rendered disabled (not hidden) when there is no resolved location,
	 * e.g. Courier/Pickup before an address is entered (FR-027).
	 */
	disabled?: boolean;
	triggerClassName?: string;
	/**
	 * Desktop (md+) uses the sort-shell dropdown. Mobile listing and the
	 * fullscreen map use the Figma 547 bottom sheet with a delayed apply.
	 */
	variant?: "dropdown" | "sheet";
}

/**
 * Radius selector for the pharmacy map (MKP-2054): 10/15/20/25 km plus
 * "Any radius", each row showing the count of pharmacies inside that radius.
 * Zero-result rows are disabled (FR-016/FR-017).
 *
 * Figma 4249:80482/4269:82830: the desktop panel is not a Radix Select
 * popover anchored under the chip — it's a side panel docked to the picker's
 * right edge (back-chevron header, no chevron-flip on the trigger), the same
 * mechanism as `PharmacyPickerSortMenu`/`PharmacyPickerSortSidePanel`, which
 * this mirrors.
 */
export function RadiusSelect({
	value,
	pharmacyMarkers,
	onChange,
	disabled = false,
	triggerClassName,
	variant = "dropdown",
}: Readonly<RadiusSelectProps>) {
	const { t } = useTranslation(["product", "common"]);
	const options = useMemo(
		() => buildRadiusOptions(pharmacyMarkers),
		[pharmacyMarkers],
	);
	const [isOpen, setIsOpen] = useState(false);
	const [panelStyle, setPanelStyle] = useState<{ top: number; left: number }>();
	const [pickerContent, setPickerContent] = useState<Element | null>(null);
	const anchorRef = useRef<HTMLDivElement>(null);

	// Declared before the `variant === "sheet"` early return below so this
	// component never skips a hook call depending on props (Rules of Hooks).
	useLayoutEffect(() => {
		if (!isOpen) {
			setPickerContent(null);
			return;
		}
		const picker = pickerContentForAnchor(anchorRef.current);
		setPickerContent(picker);
		if (picker) return;
		const updatePosition = () => {
			const rect = anchorRef.current?.getBoundingClientRect();
			if (!rect) return;
			setPanelStyle(panelStyleForAnchor(rect));
		};
		updatePosition();
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);
		return () => {
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [isOpen]);

	usePanelOutsideClose({
		isOpen,
		anchorRef,
		panelTestId: PHARMACY_PICKER_RADIUS_PANEL_TEST_ID,
		onClose: () => setIsOpen(false),
	});

	if (variant === "sheet") {
		return (
			<RadiusSelectSheet
				value={value}
				pharmacyMarkers={pharmacyMarkers}
				onChange={onChange}
				disabled={disabled}
				triggerClassName={triggerClassName}
			/>
		);
	}
	const title = t("vendor.map.radius.title");
	const formatOptionLabel = (option: RadiusOptionValue): string =>
		option === ANY_RADIUS
			? t("vendor.map.radius.any")
			: t("vendor.map.radius.km", { km: option });
	/**
	 * Figma 547 collapses the unbounded option to `+25km` on the trigger while
	 * the dropdown row keeps the long label (PRD FR-015, identical in DE and EN).
	 */
	const formatTriggerLabel = (option: RadiusOptionValue): string =>
		option === ANY_RADIUS
			? t("vendor.map.radius.anyCompact")
			: t("vendor.map.radius.kmCompact", { km: option });
	const dockedToPicker = pickerContent != null;
	const close = (): void => setIsOpen(false);
	const toggleOpen = (): void => {
		if (disabled) return;
		const next = !isOpen;
		setIsOpen(next);
		setPickerContent(next ? pickerContentForAnchor(anchorRef.current) : null);
	};

	return (
		<div className="relative" ref={anchorRef}>
			{/*
			 * Figma 4249:80531/4154:74282: flat gray-200 chip, Satoshi Medium 14px
			 * (text-body-sm), content centered (`justify-center`, not
			 * space-between), width set to Hug contents (Figma's own resizing
			 * mode for this layer — confirmed via Dev Mode inspector, height is
			 * Fixed 36px but width is Hug) — the same `Chip variant="filter"`
			 * shell as the neighbouring Pay/Sort chips, not a Radix Select
			 * trigger. Figma 4246:79561;4154:74282: the open/active state adds a
			 * 2px ansay-700 border (kept transparent otherwise so the size never
			 * jumps) and flips the chevron to point up.
			 */}
			<Chip
				variant="filter"
				className={cn(
					"h-9 shrink-0 cursor-pointer justify-center gap-2 rounded-xl border-2 bg-gray-200 p-3 text-body-sm font-medium text-gray-700 stroke-none hover:stroke-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ansay-700",
					isOpen ? "border-ansay-700" : "border-transparent",
					disabled && "pointer-events-none opacity-50",
					triggerClassName,
				)}
				// biome-ignore lint/a11y/useSemanticElements: Chip is not a native button
				role="button"
				tabIndex={disabled ? -1 : 0}
				aria-label={title}
				aria-haspopup="dialog"
				aria-expanded={isOpen}
				aria-disabled={disabled || undefined}
				data-testid="pharmacy-radius-select"
				data-state={isOpen ? "open" : "closed"}
				onPointerDown={(event) => event.stopPropagation()}
				onClick={toggleOpen}
				onKeyDown={(event) => activateOnEnterOrSpace(event, toggleOpen)}
			>
				<span className="text-body-sm-emphasis">
					{formatTriggerLabel(value)}
				</span>
				<span className={cn("flex", isOpen && "rotate-180")}>
					<ChevronDownIcon size={5} />
				</span>
			</Chip>
			{isOpen
				? createPortal(
						<aside
							className={cn(
								"pointer-events-auto z-[52] flex w-[228px] flex-col gap-2 rounded-2xl bg-white p-3",
								PROMINENT_SHADOW,
								dockedToPicker ? "absolute top-0 left-full ml-2" : "fixed",
							)}
							data-testid={PHARMACY_PICKER_RADIUS_PANEL_TEST_ID}
							style={dockedToPicker ? undefined : panelStyle}
							onPointerDown={(event) => event.stopPropagation()}
						>
							<div className="-mx-3 -mt-3 flex items-center gap-2 border-b border-gray-200 p-3">
								<IconButton
									shape="circle"
									icon={<ChevronLeftIcon.Raw />}
									iconSize={6}
									aria-label={t("common:back")}
									data-testid="pharmacy-radius-back"
									onPointerDown={(event) => {
										event.preventDefault();
										event.stopPropagation();
										close();
									}}
									onClick={(event) => {
										event.stopPropagation();
										close();
									}}
								/>
								<p className="text-body-bold text-gray-900">{title}</p>
							</div>
							{options.map(({ value: option, count }) => {
								const isSelected = option === value;
								const isEmpty = count === 0;
								return (
									<button
										key={String(option)}
										type="button"
										disabled={isEmpty}
										className={cn(
											"flex h-12 w-full items-center justify-between rounded-xl px-3 text-left text-body-bold text-gray-700",
											isSelected
												? "border-2 border-ansay-500"
												: "border-2 border-transparent",
											isEmpty && "cursor-not-allowed bg-gray-100 text-gray-400",
										)}
										data-testid={`pharmacy-radius-option-${option}`}
										onPointerDown={(event) => {
											event.preventDefault();
											event.stopPropagation();
										}}
										onClick={(event) => {
											event.stopPropagation();
											onChange(option);
											close();
										}}
									>
										<span>{formatOptionLabel(option)}</span>
										<span
											className={cn(
												"text-body-bold",
												isEmpty ? "text-gray-400" : "text-gray-500",
											)}
										>
											{count}
										</span>
									</button>
								);
							})}
						</aside>,
						pickerContent ?? document.body,
					)
				: null}
		</div>
	);
}
